import { AppChatPropsFold } from "./AppChatPropsFold";
import { AppPhoneChat } from "./AppPhoneChat";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppContact } from "./AppIndex";
import { AppIcons } from "./AppIcons";
import { AppAvatarRules } from "./AppAvatars";
import { AppViews } from "./AppViews";
import { AppExportRules } from "./AppExportRules";
import { AppTranslateView } from "./AppTranslateView";
import { AppTopBarView } from "./AppTopBar";
import { AppChatSettingsView } from "./AppChatSettingsView";
import { AppPhoneWords } from "./AppPhoneWords";

// AppChatHead - THE CHAT HEADER (design item 9, 2026-09-30; STRUCTURED by the business look wave B, 2026-10-13):
//   back (an icon button to the chat list, phone only) · the contact's disc · the name · the PROPERTY ROW (`Fields` /
//   `Props`: a `dl` of small muted labels over values - Relationship, Status, Phone, Last message, Messages, Open tasks;
//   beside the name on a wide screen, a three-column grid under it on a phone) · on the right the "⋯" menu.
//   ⚠ It was one muted sentence ("relationship · Waiting on you · last message 3 days ago · 34 messages", `p.chsub`).
// The "⋯" menu (`AppMenus`) holds "Chat settings" (the dialog: archive, notifications, disappearing messages, the star,
// translation - `AppChatSettingsDialog`) and "Export chat (CSV)" (its button, id and label span unchanged - `AppExport`
// finds them by id). The export's progress note stays OUTSIDE the menu (it must stay readable after the menu shuts).
// ⛔ THE NOTE IS THE HEADER'S LAST CHILD - A FULL-WIDTH LINE OF ITS OWN UNDER THE NAME AND THE FIELDS (2026-10-13). It
// stood inside `.chacts`, beside the fields: "Saved "<name> - WhatsApp chat.csv" - 3 messages." took its width FROM the
// property row, which wrapped into a narrow column ("Kitchen / customer") at 1280 and pushed "⋯" onto its own line at 390.
// Empty, it takes no room at all (`.xportnote:empty`).
// ⚠ THE TRANSLATION CHIP (`#wac-trchip`, filled by `AppTranslateChip`) IS NOT IN THE MENU: it SAYS that this chat is
// translated ("EN ⇄ ID") - a state hidden behind "⋯" would be a send in the wrong language waiting to happen. It is
// drawn only while translation is ON, left of "⋯"; pressing it opens "Chat settings" at Translation.
// ⭐ THE PAGE'S OWN HEADER INSIDE THE FRAME WOULD SAY THE SAME THINGS AGAIN, so `FrameCss` hides its picture, its name
// and its subtitle when the page is embedded (the app's frame sheet; the page opened on its own in Dropbox is unchanged).
// What only the page knows stays: the "N open" task count, the identity line, the standing pill, Details and Insights.
// ⛔ Every word is escaped; no inline style (the CSP). Nothing here sends (decision 9).

/** One labelled value of the chat header's property row. `Kind` "wait" = drawn in the warning colour. */
export class AppChatField {
    public Label: string = "";
    public Value: string = "";
    public Kind: string = "";
    /** A field the row gives up FIRST where there is no room for all of them (Last message, Messages) - class `lo`. */
    public Low: boolean = false;
}

export class AppChatHead {
    public static readonly Id: string = "wac-chathead";
    public static readonly MenuId: string = "wac-chatmenu";

    /**
     * ⭐ THE PROPERTY ROW (business look wave B, 2026-10-13): what the app already knows about the contact, each as a
     * small label over its value - Relationship, Status, Phone, Last message, Messages, Open tasks.
     * ⛔ ONLY WHAT `AppContact` HOLDS, AND ONLY WHAT HAS A VALUE: a field with nothing to say is left out (no dash, no
     * "unknown"), and Phone is there only for a real number (`AppPhoneWords` - never a `lid:` alias).
     * A chat with no page yet says only that it is new.
     */
    public static Fields = (Who: AppContact | null, NewChat: boolean, Balance: string = ""): AppChatField[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            if (Who === null) { return []; }
            if (NewChat === true) { return [AppChatHead.Field("Status", "New chat", "")]; }
            const Out: AppChatField[] = [];
            if (nz(Who.Relationship).trim().length > 0) { Out.push(AppChatHead.Field("Relationship", Who.Relationship.trim(), "")); }
            // ⭐ EVERY STANDING IS SAID HERE (audit 2026-10-02), not only "Waiting on you": the page's own pill - which said it
            // a second time, in the frame right under this header - is hidden when the page is embedded (`AppFrameA11y.Css`).
            const Standing: string = AppViews.StandingWords(nz(Who.Standing));
            let Kind: string = "";
            if (nz(Who.Standing) === AppChatHead.OwedStanding) { Kind = "wait"; }
            if (Standing.length > 0) { Out.push(AppChatHead.Field("Status", Standing, Kind)); }
            const Phone: string = AppPhoneWords.Format(nz(Who.PersonKey));
            if (Phone.length > 0) { Out.push(AppChatHead.Field("Phone", Phone, "")); }
            if (nz(Who.LastIso).length > 0) {
                let Last: string = "Today";
                if (Who.DaysSinceLast > 0) { Last = AppViews.Ago(Who.DaysSinceLast) + " ago"; }
                Out.push(AppChatHead.Field("Last message", Last, "", true));
            }
            if (Who.Total > 0) { Out.push(AppChatHead.Field("Messages", Who.Total.toLocaleString(), "", true)); }
            if (Who.OpenTasks > 0) { Out.push(AppChatHead.Field("Open tasks", Who.OpenTasks.toLocaleString(), "")); }
            // ⭐ THE OPEN BALANCE (Revenue upgrade, `WAC_MONEY`): only when non-zero ("" = nothing to say).
            if (nz(Balance).length > 0) { Out.push(AppChatHead.Field("Balance", nz(Balance), "")); }
            // ⭐ ONLY WHEN BLOCKED (`AppChatMarks`, the phone's blocklist) - "not blocked" is no news in a header.
            if (Who.Blocked === true) { Out.push(AppChatHead.Field("Contact", "Blocked", "wait")); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatHead.Fields");
            return [];
        }
    };

    /** The index's word for "the ball is in the owner's court" - its Status value is drawn in the warning colour. */
    public static readonly OwedStanding: string = "owed";
    public static readonly PropsId: string = "wac-chatprops";

    /** DOCUMENTED EXCEPTION: a constructor of a plain value. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static Field = (Label: string, Value: string, Kind: string, Low: boolean = false): AppChatField => {
        const Out: AppChatField = new AppChatField();
        Out.Label = Label;
        Out.Value = Value;
        Out.Kind = Kind;
        Out.Low = Low;
        return Out;
    };

    /** The class of a field's `div` that the row gives up first where it has no room (`AppChatField.Low`). */
    public static readonly LowClass: string = "lo";

    /**
     * The fields as a real description list (`dl` > `div` > `dt` + `dd`), every word escaped; "" with no field.
     * ⭐ `data-n` = how many fields there are and `data-core` = how many of them always stay: the stylesheet picks the
     * column count from them, so the row is one line or two tidy ones at every width and never ends in a line holding a
     * single field (2026-10-13: six fields wrapped 3 + 2 + 1 beside the open AI pane). A count, never a word.
     */
    public static Props = (Fields: AppChatField[], Open: boolean = true): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Fields.length === 0) { return ""; }
            let Core: number = 0;
            for (const One of Fields) { if (One.Low !== true) { Core = Core + 1; } }
            const Out: string[] = ["<dl class=\"chprops\" id=\"" + AppChatHead.PropsId + "\" data-n=\"" + String(Fields.length)
                + "\" data-core=\"" + String(Core) + "\"" + AppChatHead.HiddenAttr(Open) + ">"];
            for (const One of Fields) {
                let Cls: string = "";
                if (One.Kind.length > 0) { Cls = " class=\"" + kHTMLCore.EscapeAttr(One.Kind) + "\""; }
                let Row: string = "<div>";
                if (One.Low === true) { Row = "<div class=\"" + AppChatHead.LowClass + "\">"; }
                Out.push(Row + "<dt>" + kHTMLCore.EscapeHtml(One.Label) + "</dt><dd" + Cls + ">" + kHTMLCore.EscapeHtml(One.Value) + "</dd></div>");
            }
            Out.push("</dl>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatHead.Props");
            return "";
        }
    };

    /** ` hidden` while the row is folded (`AppChatPropsFold`), else "". DOCUMENTED EXCEPTION: picks a constant. */
    public static HiddenAttr = (Open: boolean): string => {
        if (Open === true) { return ""; }
        return " hidden";
    };

    /**
     * The header. `Export` = "Export chat (CSV)" is offered (the hosted page with a key, a one-to-one chat with a page);
     * `Settings` = "Chat settings" is offered (the hosted page with a key, a chat the service can be asked about).
     * ⭐ The translation chip's slot (`AppTranslateView.Slots`) is in the header, left of "⋯": it is filled only while
     * the chat is being translated ("EN ⇄ ID") and opens "Chat settings" at Translation.
     */
    public static Html = (Who: AppContact | null, NewChat: boolean, Export: boolean, Settings: boolean = false, Balance: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Title: string = "Chat";
            let Avatar: string = "";
            if (Who !== null) { Title = Who.Title; Avatar = Who.Avatar; }
            const Open: boolean = AppChatPropsFold.IsOpen();
            const Row: string = AppChatHead.Props(AppChatHead.Fields(Who, NewChat, Balance), Open);
            return "<header class=\"back chathead\" id=\"" + AppChatHead.Id + "\"><a class=\"btn ghost icon chback\" href=\"#chats\""
                + " aria-label=\"All chats\" title=\"All chats\">" + AppIcons.Svg(AppIcons.Back, 20) + "</a>"
                + AppAvatarRules.Disc(Title, Avatar) + "<div class=\"chid\"><h1 class=\"chname\">" + kHTMLCore.EscapeHtml(Title)
                + "</h1></div>" + Row + "<div class=\"chacts\">"
                + AppChatPropsFold.Button(Row.length > 0, Open) + AppPhoneChat.HeadButtons() + AppTranslateView.Slots() + AppChatHead.Menu(Export, Settings) + "</div>" + AppExportRules.Note(Export) + "</header>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatHead.Html");
            return "";
        }
    };

    /**
     * The "⋯" menu: "Chat settings" (opens the dialog - `AppChatSettingsView.MenuItem`) and "Export chat (CSV)".
     * "" when neither is offered (no "⋯" at all). ⛔ NO ITEM CARRIES A STATE: archive, notifications, disappearing
     * messages, the star and translation are IN the dialog, where each always has a value (owner, 2026-10-13).
     */
    public static Menu = (Export: boolean, Settings: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Export !== true && Settings !== true) { return ""; }
            return AppTopBarView.Menu(AppChatHead.MenuId, "More for this chat", AppIcons.More, "menu",
                AppChatSettingsView.MenuItem(Settings) + AppExportRules.MenuButton(Export)).replace("class=\"topmenu\"", "class=\"topmenu chmenu\"");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatHead.Menu");
            return "";
        }
    };

    /**
     * The frame sheet's rule: the embedded page's own header keeps only its Details/Insights toggles (the app's header
     * above says who this is). DOCUMENTED EXCEPTION: a constant. ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static FrameCss = (): string => {
        return "html[data-wac-embedded] header.bar>.face,html[data-wac-embedded] header.bar .idn h1,"
            + "html[data-wac-embedded] header.bar .idn .l2 .sub{display:none}"
            + "html[data-wac-embedded] header.bar{padding-top:6px;padding-bottom:6px;min-height:0}"
            + "html[data-wac-embedded] header.bar .acts{margin-left:auto}";
    };
}
